import * as THREE from 'three/webgpu';
import {PATTERN_SKIP_MARKER_MAGENTA_COLOR} from '../constants/pattern-preview-visual';
import {PatternSkipMarkerMaterialFactory} from '../material/PatternSkipMarkerMaterialFactory';
import type {Viewer} from '../viewer/Viewer';
import {MoveAlongEntityReferenceResolver} from '../assembly/move-component/move';
import {LinearComponentPatternDirectionReferenceResolver} from './LinearComponentPatternDirectionReferenceResolver';
import {isFeaturePoint, isFeatureResultMesh} from '../mesh/feature';
import {isImportedTopologySelectionObject} from '../imported-model/selection';
import {isDatumPoint} from '../reference/DatumPoint';
import {
	createLinearComponentPatternInstanceKey,
	LinearComponentPatternDirectionMode,
	LinearComponentPatternPreviewObjectRole,
	LinearComponentPatternReferenceOffsetMode,
	LinearComponentPatternRotationReferencePoint,
} from './LinearComponentPatternTypes';
import type {LinearComponentPatternPreviewMarkerMetadata} from './LinearComponentPatternTypes';

/**
 * 线性阵列预览对象名称枚举。
 */
enum LinearPatternPreviewObjectName {
	/** 预览根组名称。 */
	RootGroup = '__linear_pattern_preview_group__',
	/** 预览实例组名称前缀。 */
	InstanceGroupPrefix = '__linear_pattern_preview_instance__:',
	/** 跳过实例 marker 根组名称。 */
	MarkerGroup = '__linear_pattern_preview_marker_group__',
	/** 跳过实例 marker 名称前缀。 */
	SkipMarkerPrefix = '__linear_pattern_preview_skip_marker__:',
}

/**
	 * 幽灵材质颜色（线性阵列预览使用黄色高亮）。
 */
const GHOST_MATERIAL_COLOR = 0xffd400;

/**
 * 幽灵材质透明度。
 */
const GHOST_MATERIAL_OPACITY = 0.35;

/**
 * 跳过实例 marker 已选（已跳过）颜色。
 */
const SKIP_MARKER_SKIPPED_COLOR = 0xffffff;

/**
 * 跳过实例 marker 默认缩放。
 *
 * 说明：
 * 1. 与 `SketchPointObject` 的草图点默认缩放保持同值；
 * 2. 精灵材质已关闭距离衰减，因此这里不再按包围盒大小动态放大或缩小；
 * 3. 这样可以保证 marker 呈现出与草图点一致的固定屏幕尺寸语义。
 */
const SKIP_MARKER_DEFAULT_SCALE = 0.015;

/**
 * 求解容差。
 */
const LINEAR_PATTERN_SOLVE_EPSILON = 1e-8;

/**
 * 线性零部件阵列预览更新参数。
 */
export interface LinearPatternPreviewParams {
	/** 方向 1 参考对象（运行时活引用）。 */
	direction1ReferenceObject: THREE.Object3D | null;
	/** 方向 1 旋转实例轴对象（运行时活引用）。 */
	direction1RotationAxisObject?: THREE.Object3D | null;
	/** 方向 1 是否启用旋转实例。 */
	direction1RotateInstances?: boolean;
	/** 方向 1 旋转实例角度增量（角度制）。 */
	direction1RotationAngle?: number;
	/** 方向 1 是否反转旋转实例方向。 */
	direction1ReverseRotation?: boolean;
	/**
	 * 方向 1 是否固定旋转轴。
	 *
	 * - `true`（默认）：所有阵列实例绕同一条公共旋转轴旋转；
	 * - `false`：每个实例的旋转轴沿方向 1 平移后再旋转。
	 */
	direction1FixedRotationAxis?: boolean;
	/** 方向 1 固定旋转轴时是否对齐到源。 */
	direction1AlignToSeed?: boolean;
	/** 方向 1 对齐到源时使用的参考点。 */
	direction1RotationReferencePoint?: LinearComponentPatternRotationReferencePoint;
	/** 方向 1 到参考模式的参考几何对象。 */
	direction1ReferenceGeometryObject: THREE.Object3D | null;
	/** 方向 1 到参考模式的所选参考对象。 */
	direction1SelectedReferenceObject: THREE.Object3D | null;

	/** 方向 1 是否反转阵列传播方向。 */
	direction1ReverseDirection?: boolean;

	/** 方向 1 当前定义方式。 */
	direction1Mode: LinearComponentPatternDirectionMode;
	/** 方向 1 间距（米制渲染空间单位）。 */
	direction1Spacing: number;
	/** 方向 1 实例数量（含源实例）。 */
	direction1Instances: number;
	/** 方向 1 到参考模式的末端偏移距离（米制渲染空间单位）。 */
	direction1OffsetDistance: number;
	/** 方向 1 到参考模式的起算参考类型。 */
	direction1ReferenceOffsetMode: LinearComponentPatternReferenceOffsetMode;
	/** 方向 1 到参考模式是否由间距驱动。 */
	direction1SetSpacing: boolean;
	/** 方向 1 到参考模式是否由实例数驱动。 */
	direction1SetInstances: boolean;
	/** 方向 2 参考对象（运行时活引用，可选）。 */
	direction2ReferenceObject: THREE.Object3D | null;
	/** 方向 2 到参考模式的参考几何对象。 */
	direction2ReferenceGeometryObject: THREE.Object3D | null;
	/** 方向 2 到参考模式的所选参考对象。 */
	direction2SelectedReferenceObject: THREE.Object3D | null;

	/** 方向 2 是否反转阵列传播方向。 */
	direction2ReverseDirection?: boolean;

	/** 方向 2 当前定义方式。 */
	direction2Mode: LinearComponentPatternDirectionMode;
	/** 方向 2 间距（米制渲染空间单位）。 */
	direction2Spacing: number;
	/** 方向 2 实例数量（含源实例）。 */
	direction2Instances: number;
	/** 方向 2 到参考模式的末端偏移距离（米制渲染空间单位）。 */
	direction2OffsetDistance: number;
	/** 方向 2 到参考模式的起算参考类型。 */
	direction2ReferenceOffsetMode: LinearComponentPatternReferenceOffsetMode;
	/** 方向 2 到参考模式是否由间距驱动。 */
	direction2SetSpacing: boolean;
	/** 方向 2 到参考模式是否由实例数驱动。 */
	direction2SetInstances: boolean;

	/** 方向 2 是否只复制源零部件，不复制方向 1 已生成的实例。 */
	direction2PatternSeedOnly?: boolean;

	/** 要阵列的零部件对象 ID 列表。 */
	componentObjectIds: string[];
	/** 当前已跳过的逻辑实例键列表。 */
	skippedInstanceKeys?: readonly string[];
	/** 是否显示跳过实例选择小球。 */
	showSkipInstanceMarkers?: boolean;
}

/**
 * 单个方向的已求解阵列参数。
 */
interface ResolvedLinearPatternDirection {
	/** 阵列方向单位向量。 */
	directionWorld: THREE.Vector3;
	/** 求解后的间距（渲染空间单位）。 */
	spacingInRenderSpace: number;
	/** 求解后的实例数（含源实例）。 */
	instances: number;
}

/**
 * 单个线性阵列实例的世界变换求解结果。
 */
export interface LinearPatternResolvedInstanceTransform {
	/** 世界位置。 */
	position: THREE.Vector3;
	/** 世界姿态。 */
	quaternion: THREE.Quaternion;
	/** 世界缩放。 */
	scale: THREE.Vector3;
}

/**
 * 单个线性阵列实例的内部求解结果。
 */
interface LinearPatternResolvedInstanceTransformResult extends LinearPatternResolvedInstanceTransform {
	/** 当前实例是否应用了旋转实例变换。 */
	rotationApplied: boolean;
}

/**
 * 线性零部件阵列预览渲染器。
 *
 * 职责：
 * 1. 根据方向参考对象和间距/实例数参数，克隆源零部件的可见 Mesh；
 * 2. 对每个克隆体应用半透明幽灵材质，并按方向向量偏移排列；
 * 3. 统一收口预览资源释放路径，避免控制器直接操作场景。
 */
export class LinearComponentPatternPreviewRenderer {
	/** Viewer 实例。 */
	private readonly viewer: Viewer;

	/** 到参考与旋转轴解析器，保留既有几何约束。 */
	private readonly referenceResolver: MoveAlongEntityReferenceResolver;
	/** 传播方向专用解析器，与选择、恢复和提交校验共用圆轴语义。 */
	private readonly directionReferenceResolver: LinearComponentPatternDirectionReferenceResolver;

	/** 当前预览根组。 */
	private previewGroup: THREE.Group | null = null;

	/** 跳过实例选择小球列表。 */
	private readonly skipMarkerObjects: THREE.Sprite[] = [];

	/** 预览包围盒复用对象。 */
	private readonly tempBoundingBox = new THREE.Box3();

	/** 预览包围盒中心复用对象。 */
	private readonly tempBoundingCenter = new THREE.Vector3();

	/** 预览根局部矩阵复用对象，批量实例更新时避免重复分配。 */
	private readonly previewRootMatrix = new THREE.Matrix4();

	/**
	 * 创建线性零部件阵列预览渲染器。
	 *
	 * @param viewer Viewer 实例。
	 */
	public constructor(viewer: Viewer) {
		this.viewer = viewer;
		this.referenceResolver = new MoveAlongEntityReferenceResolver(viewer);
		this.directionReferenceResolver = new LinearComponentPatternDirectionReferenceResolver(this.referenceResolver);
	}

	/**
	 * 更新预览。
	 *
	 * @param params 预览参数。
	 */
	public update(params: LinearPatternPreviewParams): void {
		this.clear();

		const {
			componentObjectIds,
			skippedInstanceKeys,
			showSkipInstanceMarkers,
		} = params;

		if (componentObjectIds.length === 0 || !params.direction1ReferenceObject) {
			return;
		}

		const componentObjects = this.collectComponentObjects(componentObjectIds);
		if (componentObjects.length === 0) {
			return;
		}

		const direction1Arrangement = this.resolvePatternDirectionArrangement(params, 1, componentObjects);
		if (!direction1Arrangement) {
			return;
		}
		const dir1 = direction1Arrangement.directionWorld;
		const direction1SpacingInRenderSpace = direction1Arrangement.spacingInRenderSpace;
		const direction1Instances = direction1Arrangement.instances;
		const direction2Arrangement = this.resolvePatternDirectionArrangement(params, 2, componentObjects);
		// 已选择的第二方向失效时应停止整组预览，不能悄悄退化为单方向阵列。
		if (params.direction2ReferenceObject && !direction2Arrangement) return;
		const dir2 = direction2Arrangement && direction2Arrangement.instances > 1
			? direction2Arrangement.directionWorld
			: null;
		const direction2SpacingInRenderSpace = direction2Arrangement?.spacingInRenderSpace ?? 0;
		const direction2Count = direction2Arrangement && direction2Arrangement.instances > 1
			? direction2Arrangement.instances
			: 1;

		const group = new THREE.Group();
		group.name = LinearPatternPreviewObjectName.RootGroup;
		const markerGroup = showSkipInstanceMarkers
			? new THREE.Group()
			: null;
		if (markerGroup) {
			markerGroup.name = LinearPatternPreviewObjectName.MarkerGroup;
			group.add(markerGroup);
		}
		const skippedInstanceKeySet = this.createSkippedInstanceKeySet(skippedInstanceKeys);
		for (let i = 0; i < direction1Instances; i += 1) {
			for (let j = 0; j < direction2Count; j += 1) {

				// 只阵列源时，方向 2 只复制源实例列，禁止复制方向 1 已派生出的阵列实例。
				if (this.shouldSkipDirection2DerivedInstance(params, i, j)) {
					continue;
				}

				if (i === 0 && j === 0) {
					continue;
				}


				// 控制器状态已统一使用米制渲染空间单位，这里只按求解后的距离叠加方向偏移。
				const offset = new THREE.Vector3();
				offset.addScaledVector(dir1, direction1SpacingInRenderSpace * i);
				if (dir2) {
					offset.addScaledVector(dir2, direction2SpacingInRenderSpace * j);
				}


				const instanceDescriptor = {
					instanceKey: createLinearComponentPatternInstanceKey(i, j),
					direction1Index: i,
					direction2Index: j,
				};
				const instanceGroup = new THREE.Group();
				instanceGroup.name = `${LinearPatternPreviewObjectName.InstanceGroupPrefix}${instanceDescriptor.instanceKey}`;
				instanceGroup.userData.linearComponentPatternPreview = {
					role: LinearComponentPatternPreviewObjectRole.InstanceGroup,
					...instanceDescriptor,
				};
				group.add(instanceGroup);


				for (const sourceObject of componentObjects) {
					const resolvedTransform = this.resolveInstanceTransform(
						sourceObject,
						offset,
						i,
						params,
					);
					const clonedObject = this.cloneComponentWithGhostMaterial(sourceObject, resolvedTransform);
					if (!clonedObject) {
						continue;
					}
					instanceGroup.add(clonedObject);


					// 多源零部件时，小球必须贴在每个预览零部件自身中心，
					// 但同一阵列格子的多个小球共享同一个逻辑 key，保证跳过语义仍按格子收口。
					const marker = markerGroup
						? this.createSkipInstanceMarker(clonedObject, {
							role: LinearComponentPatternPreviewObjectRole.SkipInstanceMarker,
							...instanceDescriptor,
							sourceObjectId: sourceObject.uuid,
						}, skippedInstanceKeySet.has(instanceDescriptor.instanceKey))
						: null;
					if (marker && markerGroup) {
						markerGroup.add(marker);
						this.skipMarkerObjects.push(marker);
					}

				}
				instanceGroup.visible = !skippedInstanceKeySet.has(instanceDescriptor.instanceKey);

			}
		}

		this.previewGroup = group;
		this.viewer.addObject(group);
	}

	/**
	 * 计算实例偏移数据。
	 *
	 * 设计说明：
	 * 1. 与 `update()` 使用完全一致的方向解析与单位换算逻辑；
	 * 2. 仅输出提交阶段需要的源对象 ID + 世界偏移，不创建预览对象；
	 * 3. 结果不包含源实例（i=0, j=0）。
	 *
	 * @param params 预览参数。
	 * @returns 实例偏移结果。
	 */
	public computeInstanceOffsets(params: LinearPatternPreviewParams): Array<{
		sourceObjectId: string;
		worldOffset: THREE.Vector3;
		worldTransform?: LinearPatternResolvedInstanceTransform;
	}> {
		const {
			componentObjectIds,
			skippedInstanceKeys,
		} = params;

		if (componentObjectIds.length === 0 || !params.direction1ReferenceObject) {
			return [];
		}

		const componentObjects = this.collectComponentObjects(componentObjectIds);
		if (componentObjects.length === 0) {
			return [];
		}

		const direction1Arrangement = this.resolvePatternDirectionArrangement(params, 1, componentObjects);
		if (!direction1Arrangement) {
			return [];
		}
		const dir1 = direction1Arrangement.directionWorld;
		const direction1SpacingInRenderSpace = direction1Arrangement.spacingInRenderSpace;
		const direction1Instances = direction1Arrangement.instances;
		const direction2Arrangement = this.resolvePatternDirectionArrangement(params, 2, componentObjects);
		// 与预览保持一致：圆轴失效后禁止提交只剩方向 1 的部分结果。
		if (params.direction2ReferenceObject && !direction2Arrangement) return [];
		const dir2 = direction2Arrangement && direction2Arrangement.instances > 1
			? direction2Arrangement.directionWorld
			: null;
		const direction2SpacingInRenderSpace = direction2Arrangement?.spacingInRenderSpace ?? 0;
		const direction2Count = direction2Arrangement && direction2Arrangement.instances > 1
			? direction2Arrangement.instances
			: 1;
		const skippedInstanceKeySet = this.createSkippedInstanceKeySet(skippedInstanceKeys);
		const offsets: Array<{
			sourceObjectId: string;
			worldOffset: THREE.Vector3;
			worldTransform?: LinearPatternResolvedInstanceTransform;
		}> = [];

		for (let i = 0; i < direction1Instances; i += 1) {
			for (let j = 0; j < direction2Count; j += 1) {

				// 提交与预览必须使用同一过滤规则，保证确认后不会生成预览中不存在的组合实例。
				if (this.shouldSkipDirection2DerivedInstance(params, i, j)) {
					continue;
				}

				if (i === 0 && j === 0) {
					continue;
				}


				const instanceKey = createLinearComponentPatternInstanceKey(i, j);
				if (skippedInstanceKeySet.has(instanceKey)) {
					continue;
				}


				const offset = new THREE.Vector3();
				offset.addScaledVector(dir1, direction1SpacingInRenderSpace * i);
				if (dir2) {
					offset.addScaledVector(dir2, direction2SpacingInRenderSpace * j);
				}

				for (const sourceObjectId of componentObjectIds) {
					const sourceObject = this.viewer.findSceneObjectById(sourceObjectId);
					if (!sourceObject) {
						continue;
					}
					const resolvedTransform = this.resolveInstanceTransform(
						sourceObject,
						offset,
						i,
						params,
					);
					offsets.push({
						sourceObjectId,
						worldOffset: offset.clone(),
						...(resolvedTransform.rotationApplied ? { worldTransform: resolvedTransform } : {}),
					});
				}
			}
		}

		return offsets;
	}

	/**
	 * 解析单个方向的阵列求解结果。
	 *
	 * @param params 预览参数。
	 * @param directionIndex 方向序号，仅允许 `1` 或 `2`。
	 * @param componentObjects 源零部件对象列表。
	 * @returns 已求解方向；当前方向无效时返回 `null`。
	 */
	private resolvePatternDirectionArrangement(
		params: LinearPatternPreviewParams,
		directionIndex: 1 | 2,
		componentObjects: readonly THREE.Object3D[],
	): ResolvedLinearPatternDirection | null {
		const referenceObject = directionIndex === 1
			? params.direction1ReferenceObject
			: params.direction2ReferenceObject;
		if (!referenceObject) {
			return null;
		}

		const directionWorld = this.resolvePatternDirectionVector(referenceObject);
		if (!directionWorld) {
			return null;
		}

		// 阵列方向图标控制的是传播方向，必须在预览与提交共用的求解入口统一反向。
		if (this.isPatternDirectionReversed(params, directionIndex)) {
			directionWorld.multiplyScalar(-1);
		}


		const mode = directionIndex === 1 ? params.direction1Mode : params.direction2Mode;
		const requestedSpacing = directionIndex === 1 ? params.direction1Spacing : params.direction2Spacing;
		const requestedInstances = directionIndex === 1 ? params.direction1Instances : params.direction2Instances;
		const referenceGeometryObject = directionIndex === 1
			? params.direction1ReferenceGeometryObject
			: params.direction2ReferenceGeometryObject;
		const selectedReferenceObject = directionIndex === 1
			? params.direction1SelectedReferenceObject
			: params.direction2SelectedReferenceObject;
		const offsetDistance = directionIndex === 1 ? params.direction1OffsetDistance : params.direction2OffsetDistance;
		const referenceOffsetMode = directionIndex === 1
			? params.direction1ReferenceOffsetMode
			: params.direction2ReferenceOffsetMode;
		const setSpacing = directionIndex === 1 ? params.direction1SetSpacing : params.direction2SetSpacing;
		const setInstances = directionIndex === 1 ? params.direction1SetInstances : params.direction2SetInstances;

		if (mode !== LinearComponentPatternDirectionMode.UpToReference || !referenceGeometryObject) {
			return {
				directionWorld,
				spacingInRenderSpace: this.resolveNonNegativeRenderSpaceDistance(requestedSpacing),
				instances: Math.max(1, Math.floor(requestedInstances)),
			};
		}

		const sourceAnchorWorld = this.resolveUpToReferenceSourceAnchor(
			componentObjects,
			referenceOffsetMode,
			selectedReferenceObject,
		);
		if (!sourceAnchorWorld) {
			return {
				directionWorld,
				spacingInRenderSpace: this.resolveNonNegativeRenderSpaceDistance(requestedSpacing),
				instances: Math.max(1, Math.floor(requestedInstances)),
			};
		}

		const referenceDistanceInRenderSpace = this.resolveProjectedDistanceToReference(
			sourceAnchorWorld,
			directionWorld,
			referenceGeometryObject,
		);
		if (referenceDistanceInRenderSpace === null) {
			return {
				directionWorld,
				spacingInRenderSpace: this.resolveNonNegativeRenderSpaceDistance(requestedSpacing),
				instances: Math.max(1, Math.floor(requestedInstances)),
			};
		}

		const availableDistanceInRenderSpace = Math.max(
			0,
			referenceDistanceInRenderSpace - this.resolveNonNegativeRenderSpaceDistance(offsetDistance),
		);
		const useSpacingDrivenSolve = setSpacing || setSpacing === setInstances;
		if (useSpacingDrivenSolve) {
			const spacingInRenderSpace = this.resolveNonNegativeRenderSpaceDistance(requestedSpacing);
			if (spacingInRenderSpace <= LINEAR_PATTERN_SOLVE_EPSILON) {
				return {
					directionWorld,
					spacingInRenderSpace,
					instances: 1,
				};
			}
			return {
				directionWorld,
				spacingInRenderSpace,
				instances: Math.max(
					1,
					Math.floor((availableDistanceInRenderSpace + LINEAR_PATTERN_SOLVE_EPSILON) / spacingInRenderSpace) + 1,
				),
			};
		}

		const instances = Math.max(1, Math.floor(requestedInstances));
		return {
			directionWorld,
			spacingInRenderSpace: instances <= 1
				? 0
				: availableDistanceInRenderSpace / (instances - 1),
			instances,
		};
	}

	/**
	 * 归一化线性阵列长度参数。
	 *
	 * 设计说明：
	 * 1. PropertyManager 负责把用户输入的毫米转换为米，控制器和预览求解器只接收米制渲染空间单位；
	 * 2. 这里禁止再次执行 mm -> m 换算，否则 50mm 会从 0.05m 被错误缩小为 0.00005m；
	 * 3. 负数距离在阵列求解中没有有效语义，因此统一收口为 0。
	 *
	 * @param distanceInRenderSpace 米制渲染空间距离。
	 * @returns 非负米制渲染空间距离。
	 */
	private resolveNonNegativeRenderSpaceDistance(distanceInRenderSpace: number): number {
		// 数字输入框与定义恢复都可能传入边界值，求解层统一保持非负距离。
		return Math.max(0, distanceInRenderSpace);
	}


	/**
	 * 判断指定方向是否启用阵列传播反向。
	 *
	 * @param params 预览参数。
	 * @param directionIndex 方向序号。
	 * @returns 启用反向时返回 `true`。
	 */
	private isPatternDirectionReversed(
		params: LinearPatternPreviewParams,
		directionIndex: 1 | 2,
	): boolean {
		return directionIndex === 1
			? params.direction1ReverseDirection === true
			: params.direction2ReverseDirection === true;
	}


	/**
	 * 判断当前二维阵列位置是否应被“只阵列源”过滤。
	 *
	 * 设计说明：
	 * 1. SolidWorks 中方向 2 的“只阵列源”表示只沿方向 2 复制原始 seed；
	 * 2. 因此 `(i > 0, j > 0)` 这类“方向 1 实例再沿方向 2 复制”的组合必须跳过；
	 * 3. `(i > 0, j = 0)` 仍属于方向 1 的正常实例，`(i = 0, j > 0)` 属于方向 2 对 seed 的复制。
	 *
	 * @param params 预览参数。
	 * @param direction1Index 方向 1 实例序号。
	 * @param direction2Index 方向 2 实例序号。
	 * @returns 当前组合是否应跳过。
	 */
	private shouldSkipDirection2DerivedInstance(
		params: LinearPatternPreviewParams,
		direction1Index: number,
		direction2Index: number,
	): boolean {
		return params.direction2PatternSeedOnly === true
			&& direction1Index > 0
			&& direction2Index > 0;
	}


	/**
	 * 解析阵列方向向量。
	 *
	 * @param referenceObject 方向参考对象。
	 * @returns 归一化方向向量；失败时返回 `null`。
	 */
	private resolvePatternDirectionVector(referenceObject: THREE.Object3D): THREE.Vector3 | null {
		const resolvedReference = this.directionReferenceResolver.resolve(referenceObject);
		if (!resolvedReference) {
			return null;
		}
		const rawDirection = resolvedReference.directionWorld ?? resolvedReference.planeNormalWorld;
		if (!rawDirection || rawDirection.lengthSq() <= LINEAR_PATTERN_SOLVE_EPSILON) {
			return null;
		}
		return rawDirection.clone().normalize();
	}

	/**
	 * 解析到参考模式的起算锚点。
	 *
	 * @param componentObjects 源零部件对象列表。
	 * @param referenceOffsetMode 起算参考类型。
	 * @param selectedReferenceObject 所选源参考对象。
	 * @returns 起算锚点；失败时返回 `null`。
	 */
	private resolveUpToReferenceSourceAnchor(
		componentObjects: readonly THREE.Object3D[],
		referenceOffsetMode: LinearComponentPatternReferenceOffsetMode,
		selectedReferenceObject: THREE.Object3D | null,
	): THREE.Vector3 | null {
		if (referenceOffsetMode === LinearComponentPatternReferenceOffsetMode.SelectedReference) {
			const selectedReferenceAnchor = this.resolveReferenceAnchorWorld(selectedReferenceObject);
			if (selectedReferenceAnchor) {
				return selectedReferenceAnchor;
			}
		}
		return this.resolveComponentBoundingCenter(componentObjects);
	}

	/**
	 * 解析零部件整体包围盒中心。
	 *
	 * @param componentObjects 源零部件对象列表。
	 * @returns 世界包围盒中心；无法计算时返回 `null`。
	 */
	private resolveComponentBoundingCenter(componentObjects: readonly THREE.Object3D[]): THREE.Vector3 | null {
		this.tempBoundingBox.makeEmpty();
		componentObjects.forEach((componentObject) => {
			this.tempBoundingBox.expandByObject(componentObject, true);
		});
		if (this.tempBoundingBox.isEmpty()) {
			return null;
		}
		return this.tempBoundingBox.getCenter(this.tempBoundingCenter).clone();
	}

	/**
	 * 解析参考对象锚点。
	 *
	 * @param object 参考对象。
	 * @returns 世界锚点；失败时返回 `null`。
	 */
	private resolveReferenceAnchorWorld(object: THREE.Object3D | null): THREE.Vector3 | null {
		if (!object) {
			return null;
		}
		if (isFeaturePoint(object)) {
			return object.getWorldPoint();
		}
		if (isDatumPoint(object)) {
			return object.getWorldPosition(new THREE.Vector3());
		}
		const resolvedReference = this.referenceResolver.resolve(object);
		if (resolvedReference?.anchorWorld) {
			return resolvedReference.anchorWorld.clone();
		}
		return object.getWorldPosition(new THREE.Vector3());
	}

	/**
	 * 计算从起算锚点沿阵列方向到参考几何的投影距离。
	 *
	 * @param sourceAnchorWorld 起算锚点。
	 * @param directionWorld 阵列方向。
	 * @param referenceObject 参考几何对象。
	 * @returns 沿方向的投影距离；失败时返回 `null`。
	 */
	private resolveProjectedDistanceToReference(
		sourceAnchorWorld: THREE.Vector3,
		directionWorld: THREE.Vector3,
		referenceObject: THREE.Object3D | null,
	): number | null {
		if (!referenceObject) {
			return null;
		}

		if (isFeaturePoint(referenceObject) || isDatumPoint(referenceObject)) {
			const pointAnchor = this.resolveReferenceAnchorWorld(referenceObject);
			if (!pointAnchor) {
				return null;
			}
			return pointAnchor.sub(sourceAnchorWorld).dot(directionWorld);
		}

		const resolvedReference = this.referenceResolver.resolve(referenceObject);
		if (!resolvedReference) {
			const fallbackAnchor = this.resolveReferenceAnchorWorld(referenceObject);
			if (!fallbackAnchor) {
				return null;
			}
			return fallbackAnchor.sub(sourceAnchorWorld).dot(directionWorld);
		}

		if (resolvedReference.planeNormalWorld) {
			return this.resolveProjectedDistanceToPlane(
				sourceAnchorWorld,
				directionWorld,
				resolvedReference.anchorWorld,
				resolvedReference.planeNormalWorld,
			);
		}

		if (resolvedReference.directionWorld) {
			return this.resolveProjectedDistanceToLine(
				sourceAnchorWorld,
				directionWorld,
				resolvedReference.anchorWorld,
				resolvedReference.directionWorld,
			);
		}

		return resolvedReference.anchorWorld.clone().sub(sourceAnchorWorld).dot(directionWorld);
	}

	/**
	 * 计算方向射线与平面参考的投影距离。
	 *
	 * @param sourceAnchorWorld 起算锚点。
	 * @param directionWorld 阵列方向。
	 * @param planeAnchorWorld 平面锚点。
	 * @param planeNormalWorld 平面法向。
	 * @returns 投影距离。
	 */
	private resolveProjectedDistanceToPlane(
		sourceAnchorWorld: THREE.Vector3,
		directionWorld: THREE.Vector3,
		planeAnchorWorld: THREE.Vector3,
		planeNormalWorld: THREE.Vector3,
	): number {
		const denominator = directionWorld.dot(planeNormalWorld);
		if (Math.abs(denominator) <= LINEAR_PATTERN_SOLVE_EPSILON) {
			return planeAnchorWorld.clone().sub(sourceAnchorWorld).dot(directionWorld);
		}
		return planeAnchorWorld.clone().sub(sourceAnchorWorld).dot(planeNormalWorld) / denominator;
	}

	/**
	 * 计算方向射线到线性参考的最近点投影距离。
	 *
	 * @param sourceAnchorWorld 起算锚点。
	 * @param directionWorld 阵列方向。
	 * @param lineAnchorWorld 线性参考锚点。
	 * @param lineDirectionWorld 线性参考方向。
	 * @returns 投影距离。
	 */
	private resolveProjectedDistanceToLine(
		sourceAnchorWorld: THREE.Vector3,
		directionWorld: THREE.Vector3,
		lineAnchorWorld: THREE.Vector3,
		lineDirectionWorld: THREE.Vector3,
	): number {
		const normalizedLineDirection = lineDirectionWorld.clone().normalize();
		const delta = sourceAnchorWorld.clone().sub(lineAnchorWorld);
		const directionDot = directionWorld.dot(normalizedLineDirection);
		const deltaDotDirection = directionWorld.dot(delta);
		const deltaDotLine = normalizedLineDirection.dot(delta);
		const denominator = 1 - directionDot * directionDot;
		if (Math.abs(denominator) <= LINEAR_PATTERN_SOLVE_EPSILON) {
			return lineAnchorWorld.clone().sub(sourceAnchorWorld).dot(directionWorld);
		}
		return (directionDot * deltaDotLine - deltaDotDirection) / denominator;
	}


	/**
	 * 求解单个阵列实例的完整世界变换。
	 *
	 * 设计说明：
	 * 1. 普通线性阵列只平移源对象，姿态与缩放保持不变；
	 * 2. 启用固定旋转轴时，实例绕公共轴旋转；清除固定旋转轴后，旋转轴随方向 1 线性平移；
	 * 3. 固定旋转轴且启用“对齐到源”时，实例保持源零部件姿态，仅用参考点计算绕轴后的定位；
	 * 4. 若旋转轴缺失，则回退为纯线性变换，避免生成错误姿态。
	 *
	 * @param sourceObject 源零部件对象。
	 * @param worldOffset 当前实例的线性偏移。
	 * @param direction1Index 方向 1 实例序号。
	 * @param params 预览参数。
	 * @returns 当前实例的完整世界变换。
	 */
	private resolveInstanceTransform(
		sourceObject: THREE.Object3D,
		worldOffset: THREE.Vector3,
		direction1Index: number,
		params: LinearPatternPreviewParams,
	): LinearPatternResolvedInstanceTransformResult {
		const sourceWorldPosition = sourceObject.getWorldPosition(new THREE.Vector3());
		const sourceWorldQuaternion = sourceObject.getWorldQuaternion(new THREE.Quaternion());
		const sourceWorldScale = sourceObject.getWorldScale(new THREE.Vector3());
		const translatedPosition = sourceWorldPosition.clone().add(worldOffset);
		const baseTransform = {
			position: translatedPosition,
			quaternion: sourceWorldQuaternion,
			scale: sourceWorldScale,
			rotationApplied: false,
		};

		if (
			params.direction1RotateInstances !== true
			|| direction1Index <= 0
			|| params.direction1Mode === LinearComponentPatternDirectionMode.UpToReference
		) {
			return baseTransform;
		}

		const angleDegree = params.direction1RotationAngle ?? 0;
		if (Math.abs(angleDegree) <= LINEAR_PATTERN_SOLVE_EPSILON) {
			return baseTransform;
		}

		const rotationAxis = this.resolveRotationAxis(params.direction1RotationAxisObject ?? null);
		if (!rotationAxis) {
			return baseTransform;
		}

		const signedAngle = THREE.MathUtils.degToRad(angleDegree)
			* direction1Index
			* (params.direction1ReverseRotation === true ? -1 : 1);
		// 固定旋转轴（默认）：所有实例绕同一条公共轴旋转，旋转轴锚点不变。
		// 非固定旋转轴：每个实例的旋转轴沿方向 1 平移，旋转轴锚点随实例偏移。
		const useFixedAxis = params.direction1FixedRotationAxis !== false;
		const axisAnchor = useFixedAxis
			? rotationAxis.anchorWorld.clone()
			: rotationAxis.anchorWorld.clone().add(worldOffset);
		const rotationQuaternion = new THREE.Quaternion().setFromAxisAngle(rotationAxis.directionWorld, signedAngle);
		if (useFixedAxis && params.direction1AlignToSeed === true) {
			const sourceReferencePoint = this.resolveRotationReferencePoint(
				sourceObject,
				params.direction1RotationReferencePoint ?? LinearComponentPatternRotationReferencePoint.BoundingBoxCenter,
			);
			const translatedReferencePoint = sourceReferencePoint.clone().add(worldOffset);
			const rotatedReferencePoint = translatedReferencePoint
				.clone()
				.sub(axisAnchor)
				.applyQuaternion(rotationQuaternion)
				.add(axisAnchor);
			const alignPositionDelta = rotatedReferencePoint.sub(translatedReferencePoint);

			return {
				position: translatedPosition.clone().add(alignPositionDelta),
				quaternion: sourceWorldQuaternion,
				scale: sourceWorldScale,
				rotationApplied: true,
			};
		}

		const rotatedPosition = translatedPosition
			.clone()
			.sub(axisAnchor)
			.applyQuaternion(rotationQuaternion)
			.add(axisAnchor);

		return {
			position: rotatedPosition,
			quaternion: rotationQuaternion.multiply(sourceWorldQuaternion),
			scale: sourceWorldScale,
			rotationApplied: true,
		};
	}

	/**
	 * 解析旋转实例轴。
	 *
	 * @param axisObject 旋转轴参考对象。
	 * @returns 旋转轴锚点与方向；对象不是线状或面状参考时返回 `null`。
	 */
	private resolveRotationAxis(axisObject: THREE.Object3D | null): {
		/** 旋转轴锚点。 */
		anchorWorld: THREE.Vector3;
		/** 旋转轴单位方向。 */
		directionWorld: THREE.Vector3;
	} | null {
		const resolvedReference = this.referenceResolver.resolve(axisObject);
		if (!resolvedReference) {
			return null;
		}
		if (resolvedReference.directionWorld) {
			return this.createRotationAxis(resolvedReference.anchorWorld, resolvedReference.directionWorld);
		}
		if (!resolvedReference.planeNormalWorld) {
			return null;
		}
		const planeCenterWorld = this.resolvePlaneRotationAxisAnchor(resolvedReference.object)
			?? resolvedReference.anchorWorld.clone();
		return this.createRotationAxis(planeCenterWorld, resolvedReference.planeNormalWorld);
	}

	/**
	 * 创建标准化旋转轴。
	 *
	 * @param anchorWorld 旋转轴锚点。
	 * @param directionWorld 旋转轴方向。
	 * @returns 旋转轴；方向无效时返回 `null`。
	 */
	private createRotationAxis(
		anchorWorld: THREE.Vector3,
		directionWorld: THREE.Vector3,
	): {
		/** 旋转轴锚点。 */
		anchorWorld: THREE.Vector3;
		/** 旋转轴单位方向。 */
		directionWorld: THREE.Vector3;
	} | null {
		const normalizedDirectionWorld = directionWorld.clone();
		if (normalizedDirectionWorld.lengthSq() <= LINEAR_PATTERN_SOLVE_EPSILON) {
			return null;
		}
		return {
			anchorWorld: anchorWorld.clone(),
			directionWorld: normalizedDirectionWorld.normalize(),
		};
	}

	/**
	 * 解析面状旋转参考的中心点。
	 *
	 * 设计说明：
	 * 1. 特征面旋转轴语义是“穿过面中心并垂直于面”的线；
	 * 2. 面对象已有可见几何时优先使用世界包围盒中心，贴近用户看到的选中面中心；
	 * 3. 基准面或无有效包围盒对象则由调用方回退到 resolver 提供的平面锚点。
	 *
	 * @param planeObject 面状参考对象。
	 * @returns 面中心；无法计算时返回 `null`。
	 */
	private resolvePlaneRotationAxisAnchor(planeObject: THREE.Object3D | null): THREE.Vector3 | null {
		if (!planeObject) {
			return null;
		}
		this.tempBoundingBox.makeEmpty();
		this.tempBoundingBox.setFromObject(planeObject, true);
		if (this.tempBoundingBox.isEmpty()) {
			return null;
		}
		return this.tempBoundingBox.getCenter(this.tempBoundingCenter).clone();
	}

	/**
	 * 解析旋转实例对齐到源时使用的参考点。
	 *
	 * 设计说明：
	 * 1. “零部件原点”使用源对象世界位置，与 Three.js Object3D 原点一致；
	 * 2. “边界框中心”使用源零部件可见包围盒中心，更贴近 SOLIDWORKS 默认的视觉中心语义；
	 * 3. 若包围盒退化，则回退到零部件原点，避免对齐分支产生无效位置。
	 *
	 * @param sourceObject 源零部件对象。
	 * @param referencePoint 参考点类型。
	 * @returns 世界坐标参考点。
	 */
	private resolveRotationReferencePoint(
		sourceObject: THREE.Object3D,
		referencePoint: LinearComponentPatternRotationReferencePoint,
	): THREE.Vector3 {
		if (referencePoint === LinearComponentPatternRotationReferencePoint.ComponentOrigin) {
			return sourceObject.getWorldPosition(new THREE.Vector3());
		}

		this.tempBoundingBox.makeEmpty();
		this.tempBoundingBox.setFromObject(sourceObject, true);
		if (this.tempBoundingBox.isEmpty()) {
			return sourceObject.getWorldPosition(new THREE.Vector3());
		}
		return this.tempBoundingBox.getCenter(this.tempBoundingCenter).clone();
	}


	/**
	 * 清理当前预览对象并释放 GPU 资源。
	 */
	public clear(): void {
		this.skipMarkerObjects.length = 0;
		if (!this.previewGroup) {
			return;
		}

		this.viewer.scene.remove(this.previewGroup);

		this.previewGroup.traverse((object) => {
			if (!(object instanceof THREE.Mesh) && !(object instanceof THREE.Sprite)) {
				return;
			}
			const material = object.material;
			if (Array.isArray(material)) {
				material.forEach((singleMaterial) => singleMaterial.dispose());
				return;
			}
			if (material instanceof THREE.Material) {
				material.dispose();
			}
		});

		this.previewGroup.clear();
		this.previewGroup = null;
	}

	/**
	 * 释放渲染器资源。
	 */
	public dispose(): void {
		this.clear();
	}

	/**
	 * 获取当前所有跳过实例选择小球。
	 *
	 * @returns marker 列表。
	 */
	public getSkipInstanceMarkerObjects(): THREE.Sprite[] {
		return [...this.skipMarkerObjects];
	}

	/**
	 * 根据跳过实例选择小球解析逻辑实例键。
	 *
	 * @param object 当前对象。
	 * @returns 逻辑实例键；不是 marker 时返回 `null`。
	 */
	public resolveSkipInstanceKeyByMarker(object: THREE.Object3D): string | null {
		return this.resolveSkipMarkerMetadata(object)?.instanceKey ?? null;
	}

	/**
	 * 从场景中收集指定零部件对象。
	 *
	 * 设计说明：
	 * 返回零部件对象本身（包含其完整的子树），而不是其内部的 Mesh。
	 * 这样可以确保克隆时保持零件的结构和所有子节点。
	 *
	 * @param componentObjectIds 零部件对象 ID 列表。
	 * @returns 零部件对象列表。
	 */
	private collectComponentObjects(componentObjectIds: string[]): THREE.Object3D[] {
		const objects: THREE.Object3D[] = [];
		for (const objectId of componentObjectIds) {
			const componentObject = this.viewer.findSceneObjectById(objectId);
			if (componentObject) {
				objects.push(componentObject);
			}
		}
		return objects;
	}

	/**
	 * 克隆零部件对象并应用幽灵材质与世界变换。
	 *
	 * 设计说明：
	 * 1. 父节点（包括普通 Mesh）也参与克隆，保持上层结构；
	 * 2. 当递归到 FeatureResultMesh 时使用专用克隆并在该节点收口，不再继续向下；
	 * 3. 预览根对象采用求解后的世界变换，避免父子层级导致 local/world 混用；
	 * 4. 实例 source Mesh 会关闭 matrixWorldAutoUpdate，预览克隆必须恢复世界矩阵更新并同步
	 *    position/quaternion/scale，否则局部矩阵虽有偏移，渲染与 Inspector 仍读取源对象旧世界矩阵。
	 *
	 * @param sourceObject 源零部件对象。
	 * @param worldTransform 世界变换。
	 * @returns 预览对象。
	 */
	private cloneComponentWithGhostMaterial(
		sourceObject: THREE.Object3D,
		worldTransform: LinearPatternResolvedInstanceTransform,
	): THREE.Object3D {

		const workingObject = this.cloneObjectUntilFeatureResultMesh(sourceObject);

		workingObject.matrixAutoUpdate = false;
		workingObject.matrixWorldAutoUpdate = true;
		this.previewRootMatrix.compose(
			worldTransform.position,
			worldTransform.quaternion,
			worldTransform.scale,
		);
		workingObject.matrix.copy(this.previewRootMatrix);
		this.previewRootMatrix.decompose(
			workingObject.position,
			workingObject.quaternion,
			workingObject.scale,
		);
		// 预览根已关闭自动矩阵更新，直接写入局部矩阵后必须主动失效世界矩阵缓存。
		// 跳过实例 marker 会在对象加入场景前读取世界包围盒，否则会错误沿用源对象的旧世界位置。
		workingObject.matrixWorldNeedsUpdate = true;

		return workingObject;

	}

	/**
	 * 按层级克隆对象，遇到 FeatureResultMesh 即收口。
	 *
	 * 设计说明：
	 * 1. 普通节点仅做浅克隆（不自动克隆 children），再手动递归附加子节点；
	 * 2. FeatureResultMesh 使用专用克隆入口并直接返回，不再向下递归；
	 * 3. 所有 Mesh 统一替换为幽灵材质，保持预览视觉一致。
	 *
	 * @param sourceObject 源对象。
	 * @returns 克隆对象。
	 */
	private cloneObjectUntilFeatureResultMesh(sourceObject: THREE.Object3D): THREE.Object3D {
		if (isFeatureResultMesh(sourceObject)) {
			const featureClone = sourceObject.cloneForPatternPreview();
			featureClone.material = this.createGhostMaterial();
			// FeatureResultMesh 预览进入新父链后必须重算世界矩阵，不能继承 source suppression 状态。
			featureClone.matrixWorldAutoUpdate = true;
			featureClone.matrixWorldNeedsUpdate = true;
			return featureClone;
		}

		const clonedObject = sourceObject.clone(false);
		// 所有预览克隆都属于新的派生层级，必须恢复世界矩阵自动更新以消费新父链。
		clonedObject.matrixWorldAutoUpdate = true;
		clonedObject.matrixWorldNeedsUpdate = true;
		if (sourceObject instanceof THREE.Mesh && clonedObject instanceof THREE.Mesh) {
			clonedObject.material = this.createGhostMaterial();
			/**
			 * 正式 InstancedMesh 发布后会把业务 source Mesh 设为不可见以避免双绘；
			 * 预览是独立派生对象，必须恢复该 source Mesh 原本可渲染的业务语义。
			 * 只有 Viewer 注册表仍能解析到 Unit 时才恢复，普通业务隐藏 Mesh 继续保持隐藏。
			 */
			if (this.viewer.resolveImportedModelInstancedMeshUnit(sourceObject)) {
				clonedObject.visible = true;
			}
		}

		this.viewer.listImportedModelSourceHierarchyChildren(sourceObject).forEach((child) => {
			if (isImportedTopologySelectionObject(child)) {
				// imported topology host 与点线面代理按需重建，不参与普通 Object3D 克隆。
				return;
			}
			const clonedChild = this.cloneObjectUntilFeatureResultMesh(child);
			clonedObject.add(clonedChild);
		});

		return clonedObject;
	}

	/**
	 * 创建幽灵材质。
	 *
	 * @returns 幽灵材质。
	 */
	private createGhostMaterial(): THREE.MeshStandardMaterial {
		return new THREE.MeshStandardMaterial({
			color: GHOST_MATERIAL_COLOR,
			transparent: true,
			opacity: GHOST_MATERIAL_OPACITY,
			depthWrite: false,
		});
	}

	/**
	 * 创建跳过实例键集合。
	 *
	 * @param skippedInstanceKeys 跳过实例键列表。
	 * @returns 跳过实例键集合。
	 */
	private createSkippedInstanceKeySet(skippedInstanceKeys?: readonly string[]): Set<string> {
		return new Set(skippedInstanceKeys ?? []);
	}

	/**
	 * 创建跳过实例选择小球。
	 *
	 * @param targetObject 小球对应的预览零部件对象。
	 * @param metadata marker 元数据。
	 * @param isSkipped
	 * @returns marker。
	 */
	private createSkipInstanceMarker(
		targetObject: THREE.Object3D,
		metadata: LinearComponentPatternPreviewMarkerMetadata,
		isSkipped: boolean,
	): THREE.Sprite {
		targetObject.updateWorldMatrix(true, true);
		this.tempBoundingBox.makeEmpty();
		this.tempBoundingBox.setFromObject(targetObject, true);
		if (this.tempBoundingBox.isEmpty()) {
			// 空壳节点仍可能代表可被阵列的零部件根；此时回退到对象世界原点，避免丢失恢复入口。
			targetObject.getWorldPosition(this.tempBoundingCenter);
		} else {
			this.tempBoundingBox.getCenter(this.tempBoundingCenter);
		}
		const marker = new THREE.Sprite(this.createSkipMarkerMaterial(isSkipped));
		marker.name = `${LinearPatternPreviewObjectName.SkipMarkerPrefix}${metadata.instanceKey}:${metadata.sourceObjectId}`;
		marker.position.copy(this.tempBoundingCenter);
		marker.scale.setScalar(SKIP_MARKER_DEFAULT_SCALE);
		marker.renderOrder = 10;
		marker.userData.linearComponentPatternPreview = { ...metadata };
		return marker;
	}

	/**
	 * 创建跳过实例 marker 材质。
	 *
	 * @returns marker 材质。
	 */
	private createSkipMarkerMaterial(isSkipped: boolean): THREE.SpriteNodeMaterial {
		const markerColor = isSkipped
			? SKIP_MARKER_SKIPPED_COLOR
			: PATTERN_SKIP_MARKER_MAGENTA_COLOR;
		return PatternSkipMarkerMaterialFactory.create(
			markerColor,
			'线性阵列-跳过实例-sprite-材质',
		);
	}

	/**
	 * 解析跳过实例 marker 元数据。
	 *
	 * @param object 当前对象。
	 * @returns 元数据；不是 marker 时返回 `null`。
	 */
	private resolveSkipMarkerMetadata(object: THREE.Object3D): LinearComponentPatternPreviewMarkerMetadata | null {
		const metadata = object.userData?.linearComponentPatternPreview;
		if (!metadata || metadata.role !== LinearComponentPatternPreviewObjectRole.SkipInstanceMarker) {
			return null;
		}
		return metadata as LinearComponentPatternPreviewMarkerMetadata;
	}
}
